import {
  type AssignCourierDto,
  type CourierResponse,
  type CourierCashDto,
  type CourierResultDto,
  type MyShipmentResponse,
  type MyShipmentScope,
  type ActivateDraftsDto,
  type ActivateDraftsResponse,
  type AdminProductsResponse,
  type AuthTokensResponse,
  type AdminVehicleModelResponse,
  type AnalyticsResponse,
  type AuditPage,
  type ConsignmentDetailResponse,
  type ConsignmentResponse,
  type ConsignmentStatusResult,
  type DashboardResponse,
  type SupplierResponse,
  type RoleResponse,
  type StaffListItem,
  type UserResponse,
  type BannerResponse,
  type BrandResponse,
  type CategoryResponse,
  type CategoryTreeResponse,
  type CreateBannerDto,
  type CreateDeliveryZoneDto,
  type CreateBrandDto,
  type CreateCategoryDto,
  type CreateVehicleMakeDto,
  type CreateVehicleModelDto,
  type CreateConsignmentDto,
  type CreateShopDto,
  type DeliveryZoneResponse,
  type ChangeShipmentStatusDto,
  type CreateProductDto,
  type ConfirmPaymentDto,
  type ErrorResponse,
  type ListOrdersQuery,
  type ImportRunResponse,
  type ListAdminProductsQuery,
  type ListPaymentsQuery,
  type MeResponse,
  type OrderListItem,
  type OrderPaymentState,
  type OrderStatusResponse,
  type OrderResponse,
  type PaymentQueueResponse,
  type PaymentResponse,
  type ProductMediaResponse,
  type ProductResponse,
  type RecordPaymentDto,
  type SearchSynonymResponse,
  type RegionResponse,
  type SettingResponse,
  type ShipmentDetailResponse,
  type ShopResponse,
  type StaffLoginDto,
  type StatusTransitionResponse,
  type UpdateBannerDto,
  type UpdateDeliveryZoneDto,
  type UpdateOrderStatusConfigDto,
  type UpdateShopDto,
  type UpdateBrandDto,
  type UpdateCategoryDto,
  type UpdateOperatorNoteDto,
  type UpdateProductDto,
  type UpdateVehicleModelDto,
  type UpsertSearchSynonymDto,
  type VehicleMakeResponse,
  type WorkQueueBucket,
  type WorkQueueItemsResponse,
  type WorkQueueSummaryResponse,
  type AdjustStockDto,
} from '@aps/contracts';
import { API_BASE_URL } from './env.js';

/**
 * Admin API klienti.
 *
 * Barcha javob tiplari `@aps/contracts` dan. Bu yerda qo'lda yozilgan
 * javob tipi YO'Q — backend kontraktni o'zgartirsa `tsc` shu yerda
 * yiqiladi (avtopart-contracts skill).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ErrorResponse,
  ) {
    super(payload.code);
    this.name = 'ApiError';
  }

  text(locale: 'UZ' | 'RU'): string {
    return locale === 'RU' ? this.payload.messageRu : this.payload.messageUz;
  }
}

/**
 * To'lov amali javobi.
 *
 * Server qator bilan BIRGA buyurtmaning yangi holatini qaytaradi —
 * shuning uchun klient uni qaytadan hisoblamaydi. Holatni bu yerda
 * hisoblash "qatorlardan hisoblanadi, saqlanmaydi" qoidasini klientda
 * takrorlash bo'lardi va ikki joy ajralib ketardi.
 */
export interface PaymentActionResult {
  payment: PaymentResponse;
  orderPaymentState: OrderPaymentState;
  paidTotal: string;
  grandTotal: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Multipart yuklash. `content-type` ATAYLAB qo'yilmaydi: uni brauzer
   * chegara (boundary) bilan birga o'zi yozadi. Qo'lda yozilsa server
   * chegarani topa olmaydi va so'rov 400 bo'ladi.
   */
  form?: FormData;
  signal?: AbortSignal;
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onTokensChanged: ((tokens: AuthTokensResponse | null) => void) | null = null;

export function setTokens(tokens: AuthTokensResponse | null): void {
  accessToken = tokens?.accessToken ?? null;
  refreshToken = tokens?.refreshToken ?? null;
}

/** Refresh muvaffaqiyatli bo'lsa yangi tokenlarni saqlash uchun. */
export function setTokensChangedHandler(
  handler: ((tokens: AuthTokensResponse | null) => void) | null,
): void {
  onTokensChanged = handler;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function raw(path: string, options: RequestOptions): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.form === undefined ? {} : { body: options.form }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
}

/**
 * `401` kelganda BIR MARTA refresh qilinadi va so'rov takrorlanadi.
 *
 * Bir nechta so'rov bir vaqtda `401` olsa, refresh ham bir nechta marta
 * ketadi va biri ikkinchisining tokenini bekor qiladi (server eski
 * refresh'ni darhol kuchsizlantiradi). Shuning uchun refresh YAKKA:
 * birinchisi so'rov yuboradi, qolganlari o'shani kutadi.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async (): Promise<boolean> => {
    const token = refreshToken;
    if (token === null) return false;

    const response = await raw('/api/v1/auth/refresh', {
      method: 'POST',
      body: { refreshToken: token },
    });

    if (!response.ok) {
      setTokens(null);
      onTokensChanged?.(null);
      return false;
    }

    const tokens = (await parse(response)) as AuthTokensResponse;
    setTokens(tokens);
    onTokensChanged?.(tokens);
    return true;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await raw(path, options);

  if (response.status === 401 && refreshToken !== null) {
    if (await refreshOnce()) response = await raw(path, options);
  }

  const payload = await parse(response);
  if (!response.ok) throw new ApiError(response.status, payload as ErrorResponse);
  return payload as T;
}

export const api = {
  staffLogin: (body: StaffLoginDto): Promise<AuthTokensResponse> =>
    request('/api/v1/auth/staff/login', { method: 'POST', body }),

  me: (): Promise<MeResponse> => request('/api/v1/auth/me'),

  workQueueSummary: (signal?: AbortSignal): Promise<WorkQueueSummaryResponse> =>
    request('/api/v1/admin/work-queue', { ...(signal === undefined ? {} : { signal }) }),

  workQueueItems: (
    bucket: WorkQueueBucket,
    params: { limit?: number; cursor?: string } = {},
  ): Promise<WorkQueueItemsResponse> => {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    const suffix = query.toString();
    return request(`/api/v1/admin/work-queue/${bucket}${suffix === '' ? '' : `?${suffix}`}`);
  },

  /* ---------------------------- buyurtmalar ---------------------------- */

  orders: (
    params: Partial<ListOrdersQuery> = {},
  ): Promise<{ items: OrderListItem[]; nextCursor: string | null; total: number }> => {
    // `Object.entries` bilan sikl yozilmaydi: `undefined` maydonlar jimgina
    // tushib qolardi va nima yuborilayotgani ko'rinmasdi.
    const query = new URLSearchParams();
    if (params.query !== undefined) query.set('query', params.query);
    if (params.statusCode !== undefined) query.set('statusCode', params.statusCode);
    if (params.paymentState !== undefined) query.set('paymentState', params.paymentState);
    if (params.regionId !== undefined) query.set('regionId', params.regionId);
    if (params.shopId !== undefined) query.set('shopId', params.shopId);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);

    const suffix = query.toString();
    return request(`/api/v1/admin/orders${suffix === '' ? '' : `?${suffix}`}`);
  },

  order: (id: string): Promise<OrderResponse> => request(`/api/v1/orders/${id}`),

  updateOperatorNote: (id: string, dto: UpdateOperatorNoteDto): Promise<OrderResponse> =>
    request(`/api/v1/admin/orders/${id}/note`, { method: 'PATCH', body: dto }),

  /* ----------------------------- to'lovlar ----------------------------- */

  recordPayment: (orderId: string, dto: RecordPaymentDto): Promise<PaymentActionResult> =>
    request(`/api/v1/admin/orders/${orderId}/payments`, { method: 'POST', body: dto }),

  confirmPayment: (id: string, dto: ConfirmPaymentDto): Promise<PaymentActionResult> =>
    request(`/api/v1/admin/payments/${id}/confirm`, { method: 'POST', body: dto }),

  rejectPayment: (id: string, rejectReason: string): Promise<PaymentActionResult> =>
    request(`/api/v1/admin/payments/${id}/reject`, { method: 'POST', body: { rejectReason } }),

  refundPayment: (id: string, rejectReason: string): Promise<PaymentActionResult> =>
    request(`/api/v1/admin/payments/${id}/refund`, { method: 'POST', body: { rejectReason } }),

  paymentQueue: (params: Partial<ListPaymentsQuery> = {}): Promise<PaymentQueueResponse> => {
    const query = new URLSearchParams();
    if (params.status !== undefined) query.set('status', params.status);
    if (params.method !== undefined) query.set('method', params.method);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);

    const suffix = query.toString();
    return request(`/api/v1/admin/payments${suffix === '' ? '' : `?${suffix}`}`);
  },

  /* ---------------------------- jo'natmalar ---------------------------- */

  shipment: (id: string): Promise<ShipmentDetailResponse> => request(`/api/v1/shipments/${id}`),

  /**
   * Ruxsat etilgan o'tishlar.
   *
   * Tugmalar SHU RO'YXATDAN quriladi — klientda status grafi yozilmaydi
   * (avtopart-status-machine skill: graf `status_transitions` jadvalida).
   */
  shipmentTransitions: (id: string): Promise<StatusTransitionResponse[]> =>
    request(`/api/v1/admin/shipments/${id}/transitions`),

  changeShipmentStatus: (
    id: string,
    dto: ChangeShipmentStatusDto,
  ): Promise<ShipmentDetailResponse> =>
    request(`/api/v1/admin/shipments/${id}/status`, { method: 'POST', body: dto }),

  assignShop: (id: string, shopId: string): Promise<ShipmentDetailResponse> =>
    request(`/api/v1/admin/shipments/${id}`, { method: 'PATCH', body: { shopId } }),

  shops: (): Promise<ShopResponse[]> => request('/api/v1/shops'),

  /* ---------------------------- kuryer oqimi ---------------------------- */

  /** Do'konning faol kuryerlari (biriktirish tanlovi). */
  couriers: (shopId: string): Promise<CourierResponse[]> =>
    request(`/api/v1/admin/couriers?shopId=${shopId}`),

  /** Kuryerga topshirish: kuryer + ETA, status `OUT_FOR_DELIVERY` bo'ladi. */
  assignCourier: (id: string, dto: AssignCourierDto): Promise<ShipmentDetailResponse> =>
    request(`/api/v1/admin/shipments/${id}/courier`, { method: 'POST', body: dto }),

  /** Kuryerning O'Z yetkazmalari. */
  myShipments: (scope: MyShipmentScope): Promise<MyShipmentResponse[]> =>
    request(`/api/v1/admin/my-shipments?scope=${scope}`),

  courierResult: (id: string, dto: CourierResultDto): Promise<MyShipmentResponse> =>
    request(`/api/v1/admin/my-shipments/${id}/result`, { method: 'POST', body: dto }),

  courierCash: (id: string, dto: CourierCashDto): Promise<MyShipmentResponse> =>
    request(`/api/v1/admin/my-shipments/${id}/cash`, { method: 'POST', body: dto }),

  /* --------------------- do'kon, zona, status --------------------- */

  /** Adminga — yashirilgan do'konlar ham. */
  adminShops: (): Promise<ShopResponse[]> => request('/api/v1/admin/shops'),

  createShop: (dto: CreateShopDto): Promise<ShopResponse> =>
    request('/api/v1/admin/shops', { method: 'POST', body: dto }),

  updateShop: (id: string, dto: UpdateShopDto): Promise<ShopResponse> =>
    request(`/api/v1/admin/shops/${id}`, { method: 'PATCH', body: dto }),

  adminZones: (): Promise<DeliveryZoneResponse[]> => request('/api/v1/admin/delivery-zones'),

  createZone: (dto: CreateDeliveryZoneDto): Promise<DeliveryZoneResponse> =>
    request('/api/v1/admin/delivery-zones', { method: 'POST', body: dto }),

  updateZone: (id: string, dto: UpdateDeliveryZoneDto): Promise<DeliveryZoneResponse> =>
    request(`/api/v1/admin/delivery-zones/${id}`, { method: 'PATCH', body: dto }),

  regions: (): Promise<RegionResponse[]> => request('/api/v1/regions'),

  statuses: (): Promise<OrderStatusResponse[]> => request('/api/v1/admin/statuses'),

  /* ------------------------ Xitoy partiyalari ------------------------ */

  consignments: (): Promise<ConsignmentResponse[]> => request('/api/v1/admin/consignments'),

  consignment: (id: string): Promise<ConsignmentDetailResponse> =>
    request(`/api/v1/admin/consignments/${id}`),

  createConsignment: (body: CreateConsignmentDto): Promise<ConsignmentResponse> =>
    request('/api/v1/admin/consignments', { method: 'POST', body }),

  /**
   * Partiya statusini ko'chirish.
   *
   * Javobda `movedCount` va `skipped` bo'ladi: qaysi jo'natma
   * ko'chdi, qaysi biri ko'chmadi va NEGA. Bu ekranning butun
   * qiymati — 40 ta buyurtmani qo'lda ochish o'rniga bitta bosish.
   */
  changeConsignmentStatus: (
    id: string,
    dto: ChangeShipmentStatusDto,
  ): Promise<ConsignmentStatusResult> =>
    request(`/api/v1/admin/consignments/${id}/status`, { method: 'POST', body: dto }),

  suppliers: (): Promise<SupplierResponse[]> => request('/api/v1/admin/suppliers'),

  dashboard: (): Promise<DashboardResponse> => request('/api/v1/admin/analytics/dashboard'),

  analytics: (days: number): Promise<AnalyticsResponse> =>
    request(`/api/v1/admin/analytics?days=${String(days)}`),

  staff: (q: string): Promise<StaffListItem[]> =>
    request(`/api/v1/admin/users${q.trim() === '' ? '' : `?q=${encodeURIComponent(q.trim())}`}`),

  roles: (): Promise<RoleResponse[]> => request('/api/v1/admin/roles'),

  createStaff: (dto: Record<string, unknown>): Promise<UserResponse> =>
    request('/api/v1/admin/users', { method: 'POST', body: dto }),

  assignRole: (userId: string, dto: { roleId: string; shopId?: string }): Promise<void> =>
    request(`/api/v1/admin/users/${userId}/roles`, { method: 'POST', body: dto }),

  removeRole: (userId: string, roleId: string): Promise<void> =>
    request(`/api/v1/admin/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),

  audit: (params: { entity?: string; cursor?: string }): Promise<AuditPage> => {
    const query = new URLSearchParams();
    if (params.entity !== undefined) query.set('entity', params.entity);
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    return request(`/api/v1/admin/audit?${query.toString()}`);
  },

  statusTransitions: (): Promise<StatusTransitionResponse[]> =>
    request('/api/v1/admin/statuses/transitions'),

  updateStatus: (code: string, dto: UpdateOrderStatusConfigDto): Promise<OrderStatusResponse> =>
    request(`/api/v1/admin/statuses/${code}`, { method: 'PATCH', body: dto }),

  /* ------------------------------ katalog ------------------------------ */

  /**
   * Admin tovar ro'yxati — mijoz ro'yxati EMAS.
   *
   * `/api/v1/products` `DRAFT` tovarlarni ko'rsatmaydi (ruxsat bo'lsa
   * ham matn bo'yicha qidiruv yo'q), qidiruv indeksida esa faqat
   * `ACTIVE` bor. Admin uchun alohida yo'l shu sababdan.
   */
  adminProducts: (params: Partial<ListAdminProductsQuery> = {}): Promise<AdminProductsResponse> => {
    const query = new URLSearchParams();
    if (params.query !== undefined) query.set('query', params.query);
    if (params.status !== undefined) query.set('status', params.status);
    if (params.categoryId !== undefined) query.set('categoryId', params.categoryId);
    if (params.brandId !== undefined) query.set('brandId', params.brandId);
    if (params.supplyType !== undefined) query.set('supplyType', params.supplyType);
    if (params.sort !== undefined) query.set('sort', params.sort);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);

    const suffix = query.toString();
    return request(`/api/v1/admin/products${suffix === '' ? '' : `?${suffix}`}`);
  },

  adminProduct: (id: string): Promise<ProductResponse> => request(`/api/v1/admin/products/${id}`),

  createProduct: (dto: CreateProductDto): Promise<ProductResponse> =>
    request('/api/v1/admin/products', { method: 'POST', body: dto }),

  updateProduct: (id: string, dto: UpdateProductDto): Promise<ProductResponse> =>
    request(`/api/v1/admin/products/${id}`, { method: 'PATCH', body: dto }),

  activateDrafts: (dto: ActivateDraftsDto): Promise<ActivateDraftsResponse> =>
    request('/api/v1/admin/products/activate-drafts', { method: 'POST', body: dto }),

  archiveProduct: (id: string): Promise<null> =>
    request(`/api/v1/admin/products/${id}`, { method: 'DELETE' }),

  productMedia: (productId: string): Promise<ProductMediaResponse[]> =>
    request(`/api/v1/admin/products/${productId}/media`),

  /** Qoldiqni qo'lda tuzatish: `qty` — o'zgarish (farq), yangi qoldiq emas. */
  adjustStock: (productId: string, dto: AdjustStockDto): Promise<{ qty: number }> =>
    request(`/api/v1/admin/products/${productId}/stock`, { method: 'POST', body: dto }),

  /**
   * `removeBackground` — fonni AI bilan olib tashlash (oddiy surat uchun). Standart YO'Q:
   * dizayn qilingan rasm aynan yuklangandek qoladi.
   */
  uploadProductMedia: (
    productId: string,
    file: File,
    removeBackground = false,
  ): Promise<ProductMediaResponse> => {
    const form = new FormData();
    form.append('file', file);
    const query = removeBackground ? '?removeBackground=true' : '';
    return request(`/api/v1/admin/products/${productId}/media${query}`, { method: 'POST', form });
  },

  /** Mavjud rasmlarni originaldan qayta ishlash (qayta yuklamasdan). */
  reprocessProductMedia: (
    productId: string,
    removeBackground = false,
  ): Promise<{ queued: number }> => {
    const query = removeBackground ? '?removeBackground=true' : '';
    return request(`/api/v1/admin/products/${productId}/media/reprocess${query}`, {
      method: 'POST',
    });
  },

  setPrimaryMedia: (mediaId: string): Promise<null> =>
    request(`/api/v1/admin/media/${mediaId}/primary`, { method: 'PATCH' }),

  deleteProductMedia: (mediaId: string): Promise<null> =>
    request(`/api/v1/admin/media/${mediaId}`, { method: 'DELETE' }),

  /* ------------------------------- import ------------------------------- */

  /** 1-bosqich: fayl o'qiladi va TEKSHIRILADI, bazaga tovar yozilmaydi. */
  validateImport: (file: File): Promise<ImportRunResponse> => {
    const form = new FormData();
    form.append('file', file);
    return request('/api/v1/admin/imports', { method: 'POST', form });
  },

  /** 2-bosqich: ko'rilgan natijani qo'llash. */
  applyImport: (runId: string): Promise<ImportRunResponse> =>
    request(`/api/v1/admin/imports/${runId}/apply`, { method: 'POST' }),

  categoryTree: (): Promise<CategoryTreeResponse[]> => request('/api/v1/categories'),

  brands: (): Promise<BrandResponse[]> => request('/api/v1/brands'),

  /* --------------------------- ma'lumotnomalar --------------------------- */

  createCategory: (dto: CreateCategoryDto): Promise<CategoryResponse> =>
    request('/api/v1/admin/categories', { method: 'POST', body: dto }),

  updateCategory: (id: string, dto: UpdateCategoryDto): Promise<CategoryResponse> =>
    request(`/api/v1/admin/categories/${id}`, { method: 'PATCH', body: dto }),

  deleteCategory: (id: string): Promise<null> =>
    request(`/api/v1/admin/categories/${id}`, { method: 'DELETE' }),

  createBrand: (dto: CreateBrandDto): Promise<BrandResponse> =>
    request('/api/v1/admin/brands', { method: 'POST', body: dto }),

  updateBrand: (id: string, dto: UpdateBrandDto): Promise<BrandResponse> =>
    request(`/api/v1/admin/brands/${id}`, { method: 'PATCH', body: dto }),

  settings: (): Promise<SettingResponse[]> => request('/api/v1/admin/settings'),

  banners: (): Promise<BannerResponse[]> => request('/api/v1/admin/banners'),

  createBanner: (dto: CreateBannerDto): Promise<BannerResponse> =>
    request('/api/v1/admin/banners', { method: 'POST', body: dto }),

  updateBanner: (id: string, dto: UpdateBannerDto): Promise<BannerResponse> =>
    request(`/api/v1/admin/banners/${id}`, { method: 'PATCH', body: dto }),

  deleteBanner: (id: string): Promise<null> =>
    request(`/api/v1/admin/banners/${id}`, { method: 'DELETE' }),

  /** Banner rasmi: server `1200x620` WebP'ga kesadi, eskisi almashtiriladi. */
  uploadBannerImage: (id: string, file: File): Promise<BannerResponse> => {
    const form = new FormData();
    form.append('file', file);
    return request(`/api/v1/admin/banners/${id}/image`, { method: 'POST', form });
  },

  deleteBannerImage: (id: string): Promise<BannerResponse> =>
    request(`/api/v1/admin/banners/${id}/image`, { method: 'DELETE' }),

  /** Kategoriya rasmi: server `256x256` WebP'ga (shaffoflik saqlanadi) keltiradi. */
  uploadCategoryImage: (id: string, file: File): Promise<CategoryResponse> => {
    const form = new FormData();
    form.append('file', file);
    return request(`/api/v1/admin/categories/${id}/image`, { method: 'POST', form });
  },

  deleteCategoryImage: (id: string): Promise<CategoryResponse> =>
    request(`/api/v1/admin/categories/${id}/image`, { method: 'DELETE' }),

  vehicleMakes: (): Promise<(VehicleMakeResponse & { isActive: boolean })[]> =>
    request('/api/v1/admin/vehicles/makes'),

  vehicleModels: (makeId?: string): Promise<AdminVehicleModelResponse[]> =>
    request(`/api/v1/admin/vehicles/models${makeId === undefined ? '' : `?makeId=${makeId}`}`),

  createVehicleMake: (dto: CreateVehicleMakeDto): Promise<VehicleMakeResponse> =>
    request('/api/v1/admin/vehicles/makes', { method: 'POST', body: dto }),

  createVehicleModel: (dto: CreateVehicleModelDto): Promise<AdminVehicleModelResponse> =>
    request('/api/v1/admin/vehicles/models', { method: 'POST', body: dto }),

  updateVehicleModel: (
    id: string,
    dto: UpdateVehicleModelDto,
  ): Promise<AdminVehicleModelResponse> =>
    request(`/api/v1/admin/vehicles/models/${id}`, { method: 'PATCH', body: dto }),

  synonyms: (): Promise<SearchSynonymResponse[]> => request('/api/v1/admin/synonyms'),

  upsertSynonym: (dto: UpsertSearchSynonymDto): Promise<SearchSynonymResponse> =>
    request('/api/v1/admin/synonyms', { method: 'POST', body: dto }),

  deleteSynonym: (id: string): Promise<null> =>
    request(`/api/v1/admin/synonyms/${id}`, { method: 'DELETE' }),

  updateSetting: (key: string, value: string): Promise<SettingResponse> =>
    request(`/api/v1/admin/settings/${key}`, { method: 'PATCH', body: { value } }),
};
